import {
  Form,
  DatePicker,
  Table,
  ConfigProvider,
  Row,
  Col,
  Button,
  Tooltip,
} from 'antd';
import { useTranslation } from 'react-i18next';
import React, { useEffect, useState } from 'react';
import { getCourierShiftReport } from '../../../../store/requests/reports';
import 'moment/locale/tr';
import trTR from 'antd/es/locale/tr_TR';
import { Loading } from '../../../../components';
import { useSelector } from 'react-redux';
import userHaveRolePermission from 'helpers/userHaveRolePermission';
import { useHistory } from 'react-router-dom';
import { PAGE_URLS } from 'helpers/endpoints';
import { timeDiffFormatForSort } from '../../../../helpers/utils';
import { DownloadOutlined } from '@ant-design/icons';
import { CSVLink } from 'react-csv';

const Index = ({ route }) => {
  const history = useHistory();
  const { isViewEnabled } = userHaveRolePermission(route?.name);
  if (!isViewEnabled) {
    history.push(`${PAGE_URLS.AUTH}${PAGE_URLS.CONTROL}`);
  }
  const { t } = useTranslation();
  const [fetching, setFetching] = useState(false);
  const [shiftReport, setShiftReport] = useState(null);
  const selectedActiveStoreCode =
    useSelector((state) => state.auth?.user?.activeStore?.code) ?? null;
  const selectedRegionId =
    useSelector((state) => +state.auth?.user?.activeRegion?.id) ?? null;
  const [courierShiftReport, setCourierShiftReport] = useState({
    startDate: null,
    endDate: null,
    storeCode: selectedActiveStoreCode,
    regionId: selectedRegionId,
  });

  useEffect(() => {
    if (courierShiftReport.startDate && courierShiftReport.endDate) {
      const dateRange = {
        startDate: courierShiftReport.startDate,
        endDate: courierShiftReport.endDate,
      };
      getCourierShiftReport(
        courierShiftReport.storeCode
          ? `store/${courierShiftReport.storeCode}`
          : `region/${courierShiftReport.regionId}`,
        dateRange,
      )
        .then((res) => {
          if (res.isSuccess) {
            setShiftReport(res.data ?? []);
          }
        })
        .finally(() => {
          setFetching(false);
        });
    }
  }, [courierShiftReport]);

  const columns = [
    {
      title: t('courierName'),
      dataIndex: 'nameSurname',
      key: 'nameSurname',
      sorter: (a, b) => a.nameSurname - b.nameSurname,
      showSorterTooltip: false,
    },
    {
      title: t('totalOrderCount'),
      dataIndex: 'totalOrderCount',
      key: 'totalOrderCount',
      sorter: (a, b) => a.totalOrderCount - b.totalOrderCount,
      showSorterTooltip: false,
    },
    {
      title: t('deliveredOrderCount'),
      dataIndex: 'deliveredOrderCount',
      key: 'deliveredOrderCount',
      sorter: (a, b) => a.deliveredOrderCount - b.deliveredOrderCount,
      showSorterTooltip: false,
    },
    {
      title: t('notDeliveredOrderCount'),
      dataIndex: 'notDeliveredOrderCount',
      key: 'notDeliveredOrderCount',
      sorter: (a, b) => a.notDeliveredOrderCount - b.notDeliveredOrderCount,
      showSorterTooltip: false,
    },
    {
      title: t('firstPackageTakingTime'),
      dataIndex: 'firstPackageTakingTime',
      key: 'firstPackageTakingTime',
      render: (firstPackageTakingTime) =>
        new Date(firstPackageTakingTime)
          ?.toLocaleDateString('tr-TR', { hour: '2-digit', minute: '2-digit' })
          .toString(),
      sorter: (a, b) =>
        new Date(a?.firstPackageTakingTime) -
        new Date(b?.firstPackageTakingTime),
      showSorterTooltip: false,
    },
    {
      title: t('lastPackageDeliverTime'),
      dataIndex: 'lastPackageDeliverTime',
      key: 'lastPackageDeliverTime',
      render: (lastPackageDeliverTime) =>
        new Date(lastPackageDeliverTime)?.toLocaleDateString('tr-TR', {
          hour: '2-digit',
          minute: '2-digit',
        }),
      sorter: (a, b) =>
        new Date(a?.lastPackageDeliverTime) -
        new Date(b?.lastPackageDeliverTime),
      showSorterTooltip: false,
    },
    {
      title: t('timeDiff'),
      dataIndex: 'timeDiff',
      key: 'timeDiff',
      sorter: (a, b) =>
        timeDiffFormatForSort(a?.timeDiff) - timeDiffFormatForSort(b?.timeDiff),
      showSorterTooltip: false,
    },
  ];

  const renderActionButtons = () => {
    return (
      <Tooltip title={t('download')}>
        <CSVLink
          filename={t('courierShiftReport')}
          data={[
            columns.map((item) => item.title),
            ...shiftReport?.map((item) => [
              item.nameSurname,
              item.totalOrderCount,
              item.deliveredOrderCount,
              item.notDeliveredOrderCount,
              new Date(item.firstPackageTakingTime)
                ?.toLocaleDateString('tr-TR', {
                  hour: '2-digit',
                  minute: '2-digit',
                })
                .toString(),
              new Date(item.lastPackageDeliverTime)?.toLocaleDateString(
                'tr-TR',
                { hour: '2-digit', minute: '2-digit' },
              ),
              item.timeDiff,
            ]),
          ]}
        >
          <Button
            style={{ marginRight: '3px' }}
            shape="round"
            icon={<DownloadOutlined />}
          ></Button>
        </CSVLink>
      </Tooltip>
    );
  };

  const datepickerChangeHandler = (dates, datesString) => {
    const [startDate, endDate] = datesString;
    setCourierShiftReport((prevState) => ({
      ...prevState,
      startDate,
      endDate,
    }));
  };

  return (
    <>
      <Row style={{ display: 'flex', alignItems: 'start' }}>
        <Col span={8}>
          <Col>
            <Form.Item name="range-picker" style={{ marginBottom: '10px' }}>
              <ConfigProvider locale={trTR}>
                <DatePicker.RangePicker
                  style={{ width: '90%' }}
                  allowClear={false}
                  format={'YYYY-MM-DD'}
                  onChange={datepickerChangeHandler}
                />
              </ConfigProvider>
            </Form.Item>
          </Col>
        </Col>
        <Col>{shiftReport && renderActionButtons()}</Col>
      </Row>
      {!shiftReport ? (
        fetching ? (
          <Loading />
        ) : (
          <p>{t('alert.selectDateRange')}</p>
        )
      ) : null}
      {shiftReport && (
        <>
          <Table
            loading={fetching}
            size="small"
            style={{ width: '100%' }}
            columns={columns}
            dataSource={shiftReport.map((item, index) => ({
              ...item,
              key: index.toString(),
            }))}
            pagination={{
              showSizeChanger: false,
              pageSize: 50,
            }}
            scroll={{
              y: '60vh',
            }}
          />
        </>
      )}
    </>
  );
};

export default Index;
